import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { frFR } from "@mui/x-data-grid/locales";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

import SMQMainContainer from "../../../components/SMQMainContainer";
import useSmqPermissions from "../../../hooks/useSmqPermissions";
import ConstatService from "../../../services/smq/ConstatService";
import { getDisplayedNcStatus } from "../NC/ncStatusHelper";
import HeaderRegistre from "./HeaderRegistre";
import NCDetailDialog from "./NCDetailDialog";

const EMPTY_VALUE = "—";

const CRITICITE_COLORS = {
  critique: { backgroundColor: "#fde2e2", color: "#b42318" },
  majeure: { backgroundColor: "#fff3cd", color: "#9a5b00" },
  mineure: { backgroundColor: "#d8f8e6", color: "#107844" },
};

const STATUT_COLORS = {
  delai_depasse: { backgroundColor: "#ffe1e1", color: "#d32f2f" },
  done: { backgroundColor: "#d8f8e6", color: "#107844" },
  done_after_reopen: { backgroundColor: "#d8f8e6", color: "#107844" },
  in_progress: { backgroundColor: "#fff2bf", color: "#9a5b00" },
  open: { backgroundColor: "#e1efff", color: "#175b9c" },
  reopen: { backgroundColor: "#fde2e2", color: "#b42318" },
};

const filterFieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
    borderRadius: "6px",
    fontSize: 12,
    height: 40,
  },
  "& .MuiInputLabel-root": { fontSize: 12 },
};

const filterButtonSx = {
  borderRadius: "6px",
  fontSize: 11,
  fontWeight: 700,
  height: 38,
  minWidth: 160,
  textTransform: "none",
  width: { xs: "100%", sm: 160 },
};

const normalize = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("fr");

const displayValue = (value) => {
  const text = String(value ?? "").trim();
  return text || EMPTY_VALUE;
};

const formatDate = (value) => {
  if (!value) return EMPTY_VALUE;
  const dateValue = String(value).slice(0, 10);
  const date = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("fr-FR");
};

const statusLabel = (value) => {
  if (value === "delai_depasse") return "Délai dépassé";
  if (value === "done") return "Clôturée";
  if (value === "done_after_reopen") return "Clôturée après réouverture";
  if (value === "in_progress") return "En cours";
  if (value === "open") return "Ouverte";
  if (value === "reopen") return "NC réouverte — action insuffisante";
  return displayValue(value);
};

const getEditableConstatId = (row) => {
  if (row.editable_constat_id !== undefined) {
    return row.editable_constat_id;
  }

  return row.statut !== "done" && row.statut !== "reopen"
    ? row.constat_id
    : null;
};

const RegistreList = () => {
  const { canTreat, isAdmin: isSmqAdmin } = useSmqPermissions();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [processusFilter, setProcessusFilter] = useState("");
  const [exigenceFilter, setExigenceFilter] = useState("");
  const [criticiteFilter, setCriticiteFilter] = useState("");
  const [statutFilter, setStatutFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [detailTarget, setDetailTarget] = useState(null);
  const [relationTabs, setRelationTabs] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [notification, setNotification] = useState({
    message: "",
    open: false,
    severity: "success",
  });

  const loadRegistre = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await ConstatService.getRegistre();
      if (!Array.isArray(response?.data)) {
        throw new Error("Le service du registre a retourné une réponse invalide.");
      }
      setRows(
        response.data.map((item) => ({
          ...item,
          id: item.constat_id,
          statut_affiche:
            (item.family_status || item.statut) === "done" &&
            item.has_reopened_child
              ? "done_after_reopen"
              : getDisplayedNcStatus(
                  item.family_status || item.statut,
                  item.delai
                ),
        }))
      );
    } catch (error) {
      setRows([]);
      setLoadError(error?.message || "Impossible de charger le registre.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRegistre();
  }, [loadRegistre]);

  useEffect(() => {
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, [
    criticiteFilter,
    dateFrom,
    dateTo,
    exigenceFilter,
    processusFilter,
    searchTerm,
    statutFilter,
  ]);

  const processusOptions = useMemo(
    () =>
      Array.from(new Set(rows.map((row) => row.processus).filter(Boolean))).sort(
        (left, right) => left.localeCompare(right, "fr")
      ),
    [rows]
  );

  const exigenceOptions = useMemo(
    () =>
      Array.from(new Set(rows.map((row) => row.exigence).filter(Boolean))).sort(
        (left, right) => left.localeCompare(right, "fr")
      ),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const search = normalize(searchTerm);
    return rows.filter((row) => {
      const matchesSearch =
        !search ||
        normalize(
          `${row.reference} ${row.reopened_reference || ""} ${row.description_constat} ${row.pilote}`
        ).includes(search);
      const matchesProcessus =
        !processusFilter || row.processus === processusFilter;
      const matchesExigence = !exigenceFilter || row.exigence === exigenceFilter;
      const matchesCriticite =
        !criticiteFilter || row.criticite === criticiteFilter;
      const matchesStatut =
        !statutFilter || row.statut_affiche === statutFilter;
      const constatDate = String(row.constat_date || "").slice(0, 10);
      const matchesDateFrom =
        !dateFrom || (constatDate && constatDate >= dateFrom);
      const matchesDateTo = !dateTo || (constatDate && constatDate <= dateTo);
      return (
        matchesSearch &&
        matchesProcessus &&
        matchesExigence &&
        matchesCriticite &&
        matchesStatut &&
        matchesDateFrom &&
        matchesDateTo
      );
    });
  }, [
    criticiteFilter,
    dateFrom,
    dateTo,
    exigenceFilter,
    processusFilter,
    rows,
    searchTerm,
    statutFilter,
  ]);

  const hasActiveFilters =
    searchTerm ||
    processusFilter ||
    exigenceFilter ||
    criticiteFilter ||
    statutFilter ||
    dateFrom ||
    dateTo;

  const resetFilters = () => {
    setSearchTerm("");
    setProcessusFilter("");
    setExigenceFilter("");
    setCriticiteFilter("");
    setStatutFilter("");
    setDateFrom("");
    setDateTo("");
  };

  const showNotification = (message, severity = "success") => {
    setNotification({ message, open: true, severity });
  };

  const editConstat = useCallback(
    (row) => {
      const constatId = getEditableConstatId(row);
      if (!constatId) return;
      navigate(`/smq_nc/add?constat_id=${constatId}&step=constat`);
    },
    [navigate]
  );

  const openViewDetail = useCallback((row) => setDetailTarget(row), []);
  const openOriginDetail = useCallback((row) => {
    const isChildRow = Boolean(row.parent_id);
    const originTarget = isChildRow
      ? {
          constat_id: row.parent_id,
          reference: row.parent_reference,
        }
      : row;
    const reopenedTarget = isChildRow
      ? row
      : {
          constat_id: row.reopened_constat_id,
          reference: row.reopened_reference,
        };

    setRelationTabs([
      {
        excludedConstatId: reopenedTarget.constat_id,
        label: "Origine",
        showFamilyHistory: true,
        target: originTarget,
      },
      { label: "Sous-NC", target: reopenedTarget },
    ]);
  }, []);

  const exportExcel = () => {
    const data = filteredRows.map((row) => ({
      "Code NC": displayValue(row.reference),
      Date: formatDate(row.constat_date),
      Processus: displayValue(row.processus),
      "Description du constat": displayValue(row.description_constat),
      Exigence: displayValue(row.exigence),
      Criticité: displayValue(row.criticite),
      Pilote: displayValue(row.pilote),
      Délai: formatDate(row.delai),
      Statut: statusLabel(row.statut_affiche),
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Registre NC");
    XLSX.writeFile(workbook, "registre_nc.xlsx");
  };

  const handleDelete = async () => {
    if (!isSmqAdmin || !deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await ConstatService.deleteConstat(deleteTarget.constat_id);
      setDeleteTarget(null);
      showNotification("Non-conformité mise à la corbeille avec succès.");
      await loadRegistre();
    } catch (error) {
      showNotification(error?.message || "Suppression impossible.", "error");
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "reference",
        flex: 0.95,
        headerName: "Code NC",
        minWidth: 180,
        renderCell: ({ row, value }) => (
          <Box>
            <Typography color="#10263d" fontSize={12} fontWeight={800}>
              {displayValue(value)}
            </Typography>
            {row.statut_affiche === "delai_depasse" && (
              <Typography color="#d32f2f" fontSize={10} fontWeight={700}>
                Délai dépassé
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: "constat_date",
        flex: 0.65,
        headerName: "Date",
        minWidth: 105,
        renderCell: ({ value }) => formatDate(value),
      },
      {
        field: "processus",
        flex: 0.9,
        headerName: "Processus",
        minWidth: 135,
        renderCell: ({ value }) => displayValue(value),
      },
      {
        field: "description_constat",
        flex: 1.6,
        headerName: "Description du constat",
        minWidth: 210,
        renderCell: ({ value }) => (
          <Typography fontSize={12} lineHeight={1.35}>
            {displayValue(value)}
          </Typography>
        ),
      },
      {
        field: "exigence",
        flex: 1.1,
        headerName: "Exigence",
        minWidth: 170,
        renderCell: ({ value }) => (
          <Typography fontSize={12} lineHeight={1.35}>
            {displayValue(value)}
          </Typography>
        ),
      },
      {
        field: "criticite",
        flex: 0.7,
        headerName: "Criticité",
        minWidth: 110,
        renderCell: ({ value }) => (
          <Box
            component="span"
            sx={{
              ...(CRITICITE_COLORS[value] || {
                backgroundColor: "#edf1f5",
                color: "#52677c",
              }),
              borderRadius: "999px",
              fontSize: 11,
              fontWeight: 800,
              px: 1.2,
              py: 0.5,
              textTransform: "capitalize",
            }}
          >
            {displayValue(value)}
          </Box>
        ),
      },
      {
        field: "pilote",
        flex: 0.9,
        headerName: "Pilote",
        minWidth: 140,
        renderCell: ({ value }) => displayValue(value),
      },
      {
        field: "delai",
        flex: 0.55,
        headerName: "Délai",
        minWidth: 85,
        renderCell: ({ row, value }) => (
          <Typography
            color={row.statut_affiche === "delai_depasse" ? "#d32f2f" : "#60758a"}
            fontSize={12}
          >
            {formatDate(value)}
          </Typography>
        ),
      },
      {
        field: "statut_affiche",
        flex: 0.85,
        headerName: "Statut",
        minWidth: 150,
        renderCell: ({ value }) => (
          <Box
            component="span"
            sx={{
              ...(STATUT_COLORS[value] || {
                backgroundColor: "#edf1f5",
                color: "#52677c",
              }),
              borderRadius: "999px",
              fontSize: 11,
              fontWeight: 800,
              px: 1.2,
              py: 0.5,
            }}
          >
            {statusLabel(value)}
          </Box>
        ),
      },
      {
        align: "center",
        field: "actions",
        filterable: false,
        headerAlign: "center",
        headerName: "Actions",
        minWidth: 205,
        sortable: false,
        renderCell: ({ row }) => (
          <Box sx={{ display: "flex", gap: 0.75 }}>
            <Tooltip title="Voir">
              <IconButton
                aria-label={`Voir le détail de ${row.reference}`}
                onClick={() => openViewDetail(row)}
                size="small"
                sx={{
                  backgroundColor: "rgba(0, 166, 182, 0.1)",
                  borderRadius: "8px",
                  color: "#008f9d",
                  height: 34,
                  width: 34,
                  "&:hover": { backgroundColor: "rgba(0, 166, 182, 0.18)" },
                }}
              >
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {(row.parent_id || row.reopened_constat_id) && (
              <Tooltip title="Voir l’origine et l’historique de la NC">
                <IconButton
                  aria-label={`Voir l’origine et l’historique de ${row.reference}`}
                  onClick={() => openOriginDetail(row)}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(115, 48, 223, 0.1)",
                    borderRadius: "8px",
                    color: "#7330df",
                    height: 34,
                    width: 34,
                    "&:hover": { backgroundColor: "rgba(115, 48, 223, 0.18)" },
                  }}
                >
                  <AccountTreeOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {Boolean(getEditableConstatId(row)) &&
              (row.permissions?.can_edit_declaration || canTreat) && (
              <Tooltip title="Modifier">
                <IconButton
                  aria-label={`Modifier ${row.reference}`}
                  onClick={() => editConstat(row)}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(23, 61, 97, 0.1)",
                    borderRadius: "8px",
                    color: "#173d61",
                    height: 34,
                    width: 34,
                    "&:hover": { backgroundColor: "rgba(23, 61, 97, 0.18)" },
                  }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isSmqAdmin && <Tooltip title="Supprimer">
              <IconButton
                onClick={() => setDeleteTarget(row)}
                size="small"
                sx={{
                  backgroundColor: "rgba(173, 31, 36, 0.1)",
                  borderRadius: "8px",
                  color: "#ad1f24",
                  height: 34,
                  width: 34,
                  "&:hover": { backgroundColor: "rgba(173, 31, 36, 0.18)" },
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>}
          </Box>
        ),
      },
    ],
    [canTreat, editConstat, openOriginDetail, isSmqAdmin, openViewDetail]
  );

  return (
    <SMQMainContainer>
      <>
        <HeaderRegistre
          icon={<FactCheckOutlinedIcon sx={{ color: "#d4a733", fontSize: 16 }} />}
          title="Registre des non-conformités"
        />
        <Box
          sx={{
            backgroundColor: "#f1f5fb",
            minHeight: "calc(100vh - 56px)",
            px: { xs: 1.5, sm: 2.5 },
            pb: 3,
          }}
        >
          {loadError && (
            <Alert
              action={
                <Button color="inherit" onClick={loadRegistre} size="small">
                  Réessayer
                </Button>
              }
              severity="error"
              sx={{ mb: 1.5 }}
            >
              {loadError}
            </Alert>
          )}

          <Paper
            elevation={0}
            sx={{
              border: "1px solid #cbd8e6",
              borderRadius: "8px",
              p: 1.25,
            }}
          >
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1,
                justifyContent: "space-between",
                mb: filtersVisible ? 1.5 : 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  flexWrap: "wrap",
                  gap: 1,
                  justifyContent: "flex-end",
                  ml: { sm: "auto" },
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                {filtersVisible && (
                  <Button
                    color="inherit"
                    disabled={!hasActiveFilters}
                    onClick={resetFilters}
                    size="small"
                    startIcon={<RestartAltIcon />}
                    sx={{
                      ...filterButtonSx,
                      borderColor: "#9fb1c3",
                      color: "#173d61",
                      "&:hover": {
                        backgroundColor: "#f3f7fa",
                        borderColor: "#173d61",
                      },
                    }}
                    variant="outlined"
                  >
                    Réinitialiser
                  </Button>
                )}
                <Button
                  color="inherit"
                  onClick={() => setFiltersVisible((visible) => !visible)}
                  size="small"
                  startIcon={
                    filtersVisible ? (
                      <FilterAltOffOutlinedIcon />
                    ) : (
                      <FilterAltOutlinedIcon />
                    )
                  }
                  sx={{
                    ...filterButtonSx,
                    borderColor: "#9fb1c3",
                    color: "#173d61",
                    "&:hover": {
                      backgroundColor: "#f3f7fa",
                      borderColor: "#173d61",
                    },
                  }}
                  variant="outlined"
                >
                  {filtersVisible ? "Masquer filtres" : "Afficher filtres"}
                </Button>
                <Button
                  disabled={!filteredRows.length}
                  onClick={exportExcel}
                  startIcon={<DownloadOutlinedIcon />}
                  sx={{
                    ...filterButtonSx,
                    backgroundColor: "#d5ad3f",
                    color: "#10263d",
                    fontWeight: 800,
                    "&:hover": { backgroundColor: "#c39b2e" },
                  }}
                  variant="contained"
                >
                  Télécharger Excel
                </Button>
              </Box>
            </Box>

            {filtersVisible && (
              <>
                <Typography
                  color="#52677c"
                  fontSize={10}
                  fontWeight={700}
                  letterSpacing="0.05em"
                  mb={1}
                >
                  FILTRES
                </Typography>
                <Grid alignItems="flex-start" container spacing={1.25}>
                  <Grid item md={6} sm={6} xs={12}>
                    <TextField
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon
                              sx={{ color: "#62a5bd", fontSize: 18 }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      label="Recherche"
                      onChange={(event) => setSearchTerm(event.target.value)}
                      placeholder="Référence, constat ou pilote..."
                      size="small"
                      sx={filterFieldSx}
                      value={searchTerm}
                    />
                  </Grid>
                  <Grid item md={3} sm={6} xs={12}>
                    <TextField
                      label="Processus"
                      onChange={(event) =>
                        setProcessusFilter(event.target.value)
                      }
                      select
                      size="small"
                      sx={filterFieldSx}
                      value={processusFilter}
                    >
                      <MenuItem value="">Tous les processus</MenuItem>
                      {processusOptions.map((processus) => (
                        <MenuItem key={processus} value={processus}>
                          {processus}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item md={3} sm={6} xs={12}>
                    <TextField
                      label="Exigence"
                      onChange={(event) =>
                        setExigenceFilter(event.target.value)
                      }
                      select
                      size="small"
                      sx={filterFieldSx}
                      value={exigenceFilter}
                    >
                      <MenuItem value="">Toutes les exigences</MenuItem>
                      {exigenceOptions.map((exigence) => (
                        <MenuItem key={exigence} value={exigence}>
                          {exigence}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item md={3} sm={6} xs={12}>
                    <TextField
                      label="Criticité"
                      onChange={(event) =>
                        setCriticiteFilter(event.target.value)
                      }
                      select
                      size="small"
                      sx={filterFieldSx}
                      value={criticiteFilter}
                    >
                      <MenuItem value="">Toutes les criticités</MenuItem>
                      <MenuItem value="mineure">Mineure</MenuItem>
                      <MenuItem value="majeure">Majeure</MenuItem>
                      <MenuItem value="critique">Critique</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item md={3} sm={6} xs={12}>
                    <TextField
                      label="Statut"
                      onChange={(event) => setStatutFilter(event.target.value)}
                      select
                      size="small"
                      sx={filterFieldSx}
                      value={statutFilter}
                    >
                      <MenuItem value="">Tous les statuts</MenuItem>
                      <MenuItem value="open">Ouverte</MenuItem>
                      <MenuItem value="in_progress">En cours</MenuItem>
                      <MenuItem value="reopen">Rouverte</MenuItem>
                      <MenuItem value="done">Clôturée</MenuItem>
                      <MenuItem value="done_after_reopen">
                        Clôturée après réouverture
                      </MenuItem>
                      <MenuItem value="delai_depasse">Délai dépassé</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item md={3} sm={6} xs={12}>
                    <TextField
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: dateTo || undefined }}
                      label="Du"
                      onChange={(event) => setDateFrom(event.target.value)}
                      size="small"
                      sx={filterFieldSx}
                      type="date"
                      value={dateFrom}
                    />
                  </Grid>
                  <Grid item md={3} sm={6} xs={12}>
                    <TextField
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: dateFrom || undefined }}
                      label="Au"
                      onChange={(event) => setDateTo(event.target.value)}
                      size="small"
                      sx={filterFieldSx}
                      type="date"
                      value={dateTo}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              border: "1px solid #cbd8e6",
              borderRadius: "8px",
              height: 560,
              mt: 2,
              overflow: "hidden",
            }}
          >
            <DataGrid
              columns={columns}
              disableRowSelectionOnClick
              loading={loading}
              localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
              onPaginationModelChange={setPaginationModel}
              onRowDoubleClick={({ row }) => openViewDetail(row)}
              pageSizeOptions={[10, 25, 50]}
              paginationModel={paginationModel}
              rowHeight={48}
              rows={filteredRows}
              sx={{
                border: 0,
                color: "#10263d",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 12,
                "& .MuiDataGrid-cell": { borderColor: "#d5e0eb" },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "#173d61",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 700,
                },
                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
                  outline: "2px solid #d5ad3f",
                  outlineOffset: "-2px",
                },
                "& .MuiDataGrid-columnSeparator, & .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton": {
                  color: "#fff",
                },
                "& .MuiDataGrid-row:hover": { backgroundColor: "#f6f9fc" },
                "& .MuiDataGrid-footerContainer": { borderColor: "#d5e0eb" },
              }}
            />
          </Paper>
        <NCDetailDialog
          onClose={() => setDetailTarget(null)}
          target={detailTarget}
        />
        <NCDetailDialog
          onClose={() => setRelationTabs([])}
          tabs={relationTabs}
        />

        <Dialog
          fullWidth
          maxWidth="xs"
          onClose={() => !deleting && setDeleteTarget(null)}
          open={Boolean(deleteTarget)}
        >
          <DialogTitle sx={{ pb: 0.75, textAlign: "center" }}>
            Mettre à la corbeille ?
          </DialogTitle>
          <DialogContent sx={{ textAlign: "center" }}>
            <Typography
              color="#173d61"
              fontFamily="monospace"
              fontSize={13}
              fontWeight={800}
              mb={1}
            >
              {deleteTarget?.reference}
            </Typography>
            <DialogContentText sx={{ color: "#8a99aa", fontSize: 13 }}>
              La NC sera conservée 30 jours dans la corbeille, puis supprimée
              définitivement.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 2.5 }}>
            <Button
              disabled={deleting}
              onClick={() => setDeleteTarget(null)}
              variant="outlined"
            >
              Annuler
            </Button>
            <Button
              color="error"
              disabled={deleting}
              onClick={handleDelete}
              startIcon={!deleting && <DeleteOutlineIcon />}
              variant="contained"
            >
              {deleting ? <CircularProgress color="inherit" size={20} /> : "Corbeille"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          autoHideDuration={4500}
          onClose={() => setNotification((current) => ({ ...current, open: false }))}
          open={notification.open}
        >
          <Alert
            onClose={() => setNotification((current) => ({ ...current, open: false }))}
            severity={notification.severity}
            variant="filled"
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
      </>
    </SMQMainContainer>
  );
};

export default RegistreList;
